import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createBox,
  deleteBox,
  fetchBoxList,
  fetchDetailBox,
  updateBox,
} from '@/domains/asset-management/apis/box-management';
import type {
  BoxDetailResponse,
  BoxListResponse,
  CreateBoxPayload,
  CreateBoxResponse,
  DeleteBoxResponse,
  RTUID,
  UpdateBoxParams,
  UpdateBoxPayload,
  UpdateBoxResponse,
  UseBoxManagementProps,
} from '@/domains/asset-management/types/box';
import NoticeMessage from '@/plugin/noticemessage/noticemessage.js';

const useBoxManagement = ({
  rtuID = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
}: UseBoxManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: boxListData } = useQuery<BoxListResponse>({
    queryKey: ['boxListData', queryParams],
    queryFn: () => fetchBoxList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: detailBoxData, refetch: refetchDetailBox } = useQuery<BoxDetailResponse>({
    queryKey: ['detailBoxData', rtuID],
    queryFn: () => fetchDetailBox(rtuID as RTUID),
    retry: false,
    enabled: !!rtuID,
  });

  const createBoxMutation = useMutation<CreateBoxResponse, Error, CreateBoxPayload>({
    mutationFn: (boxData) => createBox(boxData),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['boxListData', queryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteBoxMutation = useMutation<DeleteBoxResponse, Error, RTUID>({
    mutationFn: (rtuID) => deleteBox(rtuID),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['boxListData', queryParams],
          });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateBoxMutation = useMutation<
    UpdateBoxResponse,
    Error,
    {
      boxData: UpdateBoxPayload;
      params: UpdateBoxParams;
    }
  >({
    mutationFn: ({ boxData, params }) => updateBox(boxData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['boxListData', queryParams],
          });
          queryClient.invalidateQueries({
            queryKey: ['detailBoxData', responseData.data],
          });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    boxListData,
    detailBoxData,
    refetchDetailBox,
    createBox: createBoxMutation.mutate,
    updateBox: updateBoxMutation.mutate,
    deleteBox: deleteBoxMutation.mutate,
  };
};

export default useBoxManagement;
